import React, { useEffect } from 'react';
import Navbar from '../../components/Navbar';

function usePageMeta(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : '';
    let createdMeta = null;

    document.title = title;
    if (meta) {
      meta.setAttribute('content', description);
    } else {
      createdMeta = document.createElement('meta');
      createdMeta.name = 'description';
      createdMeta.content = description;
      document.head.appendChild(createdMeta);
    }

    return () => {
      document.title = prevTitle;
      if (createdMeta) {
        createdMeta.remove();
      } else if (meta) {
        meta.setAttribute('content', prevDesc || '');
      }
    };
  }, [title, description]);
}

export default function DigitalInspectionChecklists() {
  usePageMeta(
    'Digital Inspection Checklists for Operational Teams | BuildMyChecks',
    'Replace spreadsheets and paper forms with mobile-friendly digital inspection checklists. Capture consistent data, reduce rework, and keep every inspection audit-ready.'
  );

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BuildMyChecks',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'InspectionManagement',
    operatingSystem: 'Web, iOS, Android',
    description:
      'BuildMyChecks provides digital inspection checklists that replace spreadsheets and paper forms, giving teams real-time visibility, mobile inspections, and audit-ready records.',
    url: 'https://www.buildmychecks.co.uk/digital-inspection-checklists',
    offers: {
      '@type': 'Offer',
      price: '5.00',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
    },
    publisher: {
      '@type': 'Organization',
      name: 'BuildMyChecks',
      url: 'https://www.buildmychecks.co.uk',
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50 to-gray-200">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <header className="text-center mb-12">
          <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-2">Digital Ops</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">Digital Inspection Checklists for Operational Teams</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Move away from spreadsheets and paper forms with mobile-friendly digital inspection checklists. Capture consistent data, reduce rework, and keep every inspection securely stored and easy to audit.
          </p>
        </header>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why go digital with inspections?</h2>
          <p className="text-gray-700 mb-4">Paper and spreadsheets slow teams down and make it harder to spot risks early. Digital inspection checklists give you control, visibility, and confidence.</p>
          <p className="text-gray-700 mb-3 font-semibold">With BuildMyChecks, teams benefit from:</p>
          <ul className="space-y-2 text-gray-800 list-disc list-inside">
            <li>Offline-first inspections with automatic sync when back online</li>
            <li>Draft-saving so inspections are never lost mid-check</li>
            <li>Real-time insights to identify trends, defects, and risks</li>
            <li>Photo, note, and digital signature capture on any device</li>
          </ul>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Build once, use everywhere</h2>
          <p className="text-gray-700 mb-4">Create reusable checklist templates for:</p>
          <ul className="space-y-2 text-gray-800 list-disc list-inside">
            <li>Audits</li>
            <li>Quality control checks</li>
            <li>Safety walks and site inspections</li>
          </ul>
          <p className="text-gray-700 mt-4">Update a checklist once and roll changes out instantly across every team and location — no reprinting, no version confusion.</p>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Real-time visibility for managers</h2>
          <p className="text-gray-700 mb-3">See:</p>
          <ul className="space-y-2 text-gray-800 list-disc list-inside mb-4">
            <li>Which inspections are complete</li>
            <li>Which are overdue</li>
            <li>Where issues have been flagged</li>
          </ul>
          <p className="text-gray-700">Live status updates allow managers to act early, resolve problems faster, and reduce compliance risk.</p>
        </section>

        <section className="bg-gradient-to-br from-gray-50 via-emerald-50 to-white border border-emerald-100 rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Get started in four simple steps</h2>
          <ol className="list-decimal list-inside space-y-3 text-gray-800">
            <li>Create or import your inspection checklist templates</li>
            <li>Assign inspections with owners, due dates, and reminders</li>
            <li>Inspectors capture evidence on mobile, tablet, or desktop</li>
            <li>Export PDF reports and share instantly with stakeholders</li>
          </ol>
          <div className="flex flex-wrap gap-3 mt-6">
            <a href="/register" className="inline-flex items-center px-4 py-3 bg-primary text-white rounded-lg font-semibold shadow hover:bg-red-700">Replace spreadsheets with digital inspections</a>
            <a href="/features" className="inline-flex items-center px-4 py-3 bg-white text-primary border border-primary rounded-lg font-semibold shadow hover:bg-gray-50">See how digital inspections work</a>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to replace spreadsheets with digital inspections?</h2>
          <p className="text-gray-700 mb-4">Give your teams a faster, more reliable way to complete inspections — and give managers instant visibility into what is happening on the ground. Digital inspection checklists keep everything synced, searchable, and audit-ready.</p>
          <div className="flex flex-wrap gap-3">
            <a href="/register" className="inline-flex items-center px-5 py-3 bg-primary text-white rounded-lg font-semibold shadow hover:bg-red-700">Replace spreadsheets with digital inspections</a>
            <a href="/features" className="inline-flex items-center px-5 py-3 bg-white text-primary border border-primary rounded-lg font-semibold shadow hover:bg-gray-50">See how digital inspections work</a>
          </div>
        </section>
      </main>
    </div>
  );
}
